import React, { useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, MenuItem,
  Select, FormControl, InputLabel, useMediaQuery, useTheme, Tooltip, CircularProgress,
  Alert, AlertTitle
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import axios from '../api/axiosConfig';

const ROLE_MAP = {
  ROLE_ADMIN: { label: '관리자', color: 'secondary' },
  ROLE_TEACHER: { label: '교원회원', color: 'primary' },
  ROLE_USER: { label: '일반회원', color: 'default' },
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  return String(dateStr);
};

const MemberList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // 일반 등록/수정 모달 상태
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    id: null,
    loginId: '',
    password: '',
    name: '',
    email: '',
    birthDate: '',
    certNumber: '',
    role: 'ROLE_TEACHER',
    delYn: 'N',
    phone: '',
    address: '',
    infoConfirmedYn: 'N',
    passwordResetRequired: 'N'
  });

  // 엑셀 업로드 미리보기 모달 및 결과 상태
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/member/list');
      setMembers(response.data);
      setHasSearched(true);
    } catch (error) {
      console.error('회원 목록 조회 실패:', error);
      alert('회원 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(
    (m) =>
      (m.name && m.name.includes(searchTerm)) ||
      (m.loginId && m.loginId.includes(searchTerm)) ||
      (m.email && m.email.includes(searchTerm)) ||
      (m.certNumber && m.certNumber.includes(searchTerm)) ||
      (m.phone && m.phone.includes(searchTerm)) ||
      (m.birthDate && m.birthDate.includes(searchTerm)) ||
      (m.address && m.address.includes(searchTerm))
  );

  // 엑셀 파일 읽기 및 데이터 매핑
  const handleExcelFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (rows.length === 0) {
          alert('엑셀 파일에 데이터가 없습니다.');
          return;
        }

        // 엑셀 컬럼 한글/영문 매핑 처리
        const parsed = rows.map((row) => ({
          loginId: String(row['아이디'] || row['loginId'] || '').trim(),
          password: String(row['비밀번호'] || row['password'] || '').trim(),
          name: String(row['이름'] || row['name'] || '').trim(),
          email: String(row['이메일'] || row['email'] || '').trim(),
          birthDate: formatDateForInput(String(row['생년월일'] || row['birthDate'] || '').trim()),
          phone: String(row['연락처'] || row['phone'] || '').trim(),
          address: String(row['주소'] || row['address'] || '').trim(),
          certNumber: String(row['교원자격증번호'] || row['자격증번호'] || row['certNumber'] || '').trim(),
          role: String(row['권한'] || row['role'] || 'ROLE_TEACHER').trim(),
        }));

        setExcelData(parsed);
        setBulkResult(null);
        setExcelModalOpen(true);
      } catch (err) {
        console.error('엑셀 파싱 에러:', err);
        alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      } finally {
        e.target.value = ''; // 동일 파일 재업로드 가능하도록 초기화
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 엑셀 프리뷰 데이터 수정
  const handleExcelCellChange = (index, field, value) => {
    const updated = [...excelData];
    updated[index][field] = value;
    setExcelData(updated);
  };

  // 엑셀 프리뷰 특정 행 삭제
  const handleRemoveExcelRow = (index) => {
    setExcelData(excelData.filter((_, i) => i !== index));
  };

  // 엑셀 일괄 저장 요청
  const handleSaveBulkExcel = async () => {
    if (excelData.length === 0) {
      alert('저장할 회원 데이터가 없습니다.');
      return;
    }

    try {
      setBulkSaving(true);
      const payload = excelData.map((item) => ({
        ...item,
        passwordResetRequired: 'Y',
        createUser: 'admin_bulk'
      }));

      const res = await axios.post('/api/member/bulk-signup', payload);
      setBulkResult(res.data);
      if (hasSearched) fetchMembers();
    } catch (error) {
      console.error('일괄 등록 저장 에러:', error);
      alert('일괄 등록 처리 중 오류가 발생했습니다.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditMode(true);
      setSelectedMember({
        ...member,
        password: '',
        birthDate: formatDateForInput(member.birthDate),
        address: member.address || '',
        infoConfirmedYn: member.infoConfirmedYn || member.infoCheckYn || 'N',
        passwordResetRequired: member.passwordResetRequired || 'N',
      });
    } else {
      setEditMode(false);
      setSelectedMember({
        id: null,
        loginId: '',
        password: '',
        name: '',
        email: '',
        birthDate: '',
        certNumber: '',
        role: 'ROLE_TEACHER',
        delYn: 'N',
        phone: '',
        address: '',
        infoConfirmedYn: 'N',
        passwordResetRequired: 'Y'
      });
    }
    setOpenModal(true);
  };

  const handleSaveMember = async () => {
    if (!selectedMember.name || !selectedMember.email) {
      alert('이름과 이메일은 필수 입력입니다.');
      return;
    }

    try {
      if (editMode) {
        const finalResetRequired = selectedMember.password.trim() !== ''
          ? 'Y'
          : selectedMember.passwordResetRequired;

        await axios.put(`/api/member/${selectedMember.id}`, {
          name: selectedMember.name,
          email: selectedMember.email,
          birthDate: selectedMember.birthDate,
          phone: selectedMember.phone,
          address: selectedMember.address,
          certNumber: selectedMember.certNumber,
          role: selectedMember.role,
          delYn: selectedMember.delYn,
          infoConfirmedYn: selectedMember.infoConfirmedYn,
          password: selectedMember.password,
          passwordResetRequired: finalResetRequired,
          updateUser: 'admin'
        });
        alert('회원 정보가 수정되었습니다.');
      } else {
        if (!selectedMember.loginId || !selectedMember.password) {
          alert('신규 등록 시 아이디와 비밀번호는 필수 입력입니다.');
          return;
        }
        await axios.post('/api/member/signup', {
          ...selectedMember,
          createUser: 'admin'
        });
        alert('신규 회원이 등록되었습니다.');
      }
      setOpenModal(false);
      if (hasSearched) fetchMembers();
    } catch (error) {
      console.error('저장 오류:', error);
      alert(error.response?.data || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 이 회원을 삭제 처리(논리삭제) 하시겠습니까?')) {
      try {
        await axios.delete(`/api/member/${id}?adminId=admin`);
        alert('삭제 처리가 완료되었습니다.');
        if (hasSearched) fetchMembers();
      } catch (error) {
        console.error('삭제 오류:', error);
        alert('삭제 처리 중 오류가 발생했습니다.');
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            회원 관리 (관리자)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            [조회] 버튼을 눌러 회원 목록을 확인할 수 있습니다.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" color="secondary" startIcon={<RefreshIcon />} onClick={fetchMembers} disabled={loading} sx={{ borderRadius: '10px', fontWeight: 600 }}>
            {loading ? '조회 중...' : '회원 목록 조회'}
          </Button>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
            엑셀 일괄 등록
            <input type="file" accept=".xlsx, .xls" hidden onChange={handleExcelFileUpload} />
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
            신규 회원 추가
          </Button>
        </Stack>
      </Box>

      {/* 검색 바 */}
      <Card elevation={0} sx={{ p: 2, mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="이름, 아이디, 이메일, 생년월일, 주소, 자격증 번호로 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} />,
          }}
        />
      </Card>

      {/* 로딩 표시 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {/* 최초 조회 안내 메세지 */}
      {!loading && !hasSearched && (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
          <Typography color="text.secondary">
            상단의 <strong>[회원 목록 조회]</strong> 버튼을 클릭하여 회원 목록을 조회해 주세요.
          </Typography>
        </Paper>
      )}

      {/* PC/태블릿 Table View */}
      {!loading && hasSearched && !isMobile && (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1100 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>아이디</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>이메일</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>생년월일</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>연락처</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 1.5, px: 1.5 }}>주소</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>교원자격증 번호</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>정보확인</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>권한</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>상태</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1.5, px: 1.5 }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    조회된 회원 데이터가 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.loginId}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.email}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.birthDate || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.phone || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-all', minWidth: 150, py: 1.2, px: 1.5 }}>{row.address || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>{row.certNumber}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>
                      <Chip
                        label={(row.infoConfirmedYn || row.infoCheckYn) === 'Y' ? '확인완료' : '미확인'}
                        size="small"
                        color={(row.infoConfirmedYn || row.infoCheckYn) === 'Y' ? 'success' : 'warning'}
                        variant={(row.infoConfirmedYn || row.infoCheckYn) === 'Y' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>
                      <Chip label={ROLE_MAP[row.role]?.label || row.role} size="small" color={ROLE_MAP[row.role]?.color || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>
                      <Chip label={row.delYn === 'N' ? '정상' : '정지'} size="small" color={row.delYn === 'N' ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 1.2, px: 1.5 }}>
                      <Tooltip title="수정">
                        <IconButton onClick={() => handleOpenModal(row)} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton onClick={() => handleDelete(row.id)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 모바일 Card List View */}
      {!loading && hasSearched && isMobile && (
        <Stack spacing={2}>
          {filteredMembers.map((row) => (
            <Card key={row.id} elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{row.name} ({row.loginId})</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{row.email}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Chip
                    label={(row.infoConfirmedYn || row.infoCheckYn) === 'Y' ? '확인완료' : '미확인'}
                    size="small"
                    color={(row.infoConfirmedYn || row.infoCheckYn) === 'Y' ? 'success' : 'warning'}
                  />
                  <Chip label={row.delYn === 'N' ? '정상' : '정지'} size="small" color={row.delYn === 'N' ? 'success' : 'error'} />
                  <Chip label={ROLE_MAP[row.role]?.label || row.role} size="small" variant="outlined" />
                </Stack>
              </Box>
              <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: '8px', mb: 1.5, fontSize: '0.85rem' }}>
                <Typography variant="body2" color="text.secondary"><strong>생년월일:</strong> {row.birthDate || '-'}</Typography>
                <Typography variant="body2" color="text.secondary"><strong>자격증:</strong> {row.certNumber}</Typography>
                <Typography variant="body2" color="text.secondary"><strong>연락처:</strong> {row.phone || '-'}</Typography>
                <Typography variant="body2" color="text.secondary"><strong>주소:</strong> {row.address || '-'}</Typography>
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenModal(row)}>수정</Button>
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(row.id)}>삭제</Button>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* 등록/수정 모달 Dialog */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editMode ? '회원 정보 수정' : '신규 회원 등록'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editMode ? (
              <>
                <TextField fullWidth label="아이디 *" value={selectedMember.loginId} onChange={(e) => setSelectedMember({ ...selectedMember, loginId: e.target.value })} />
                <TextField fullWidth type="password" label="비밀번호 *" value={selectedMember.password} onChange={(e) => setSelectedMember({ ...selectedMember, password: e.target.value })} />
              </>
            ) : (
              <TextField
                fullWidth
                type="password"
                label="비밀번호 변경 (초기화)"
                placeholder="변경 시에만 입력하세요"
                value={selectedMember.password}
                onChange={(e) => setSelectedMember({ ...selectedMember, password: e.target.value })}
                helperText="입력 시 비밀번호가 변경되며 해당 회원은 재로그인 시 비밀번호 변경 화면으로 이동합니다."
              />
            )}

            <TextField fullWidth label="이름 *" value={selectedMember.name} onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })} />
            <TextField fullWidth label="이메일 *" value={selectedMember.email} onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })} />
            <TextField fullWidth type="date" label="생년월일" value={selectedMember.birthDate} onChange={(e) => setSelectedMember({ ...selectedMember, birthDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="연락처" value={selectedMember.phone} onChange={(e) => setSelectedMember({ ...selectedMember, phone: e.target.value })} />
            <TextField fullWidth label="주소" value={selectedMember.address} onChange={(e) => setSelectedMember({ ...selectedMember, address: e.target.value })} />
            <TextField fullWidth label="교원자격증 번호" value={selectedMember.certNumber} onChange={(e) => setSelectedMember({ ...selectedMember, certNumber: e.target.value })} />

            <FormControl fullWidth>
              <InputLabel>비밀번호 변경 강제 여부</InputLabel>
              <Select
                value={selectedMember.passwordResetRequired}
                label="비밀번호 변경 강제 여부"
                onChange={(e) => setSelectedMember({ ...selectedMember, passwordResetRequired: e.target.value })}
              >
                <MenuItem value="Y">강제 변경 필요 (Y)</MenuItem>
                <MenuItem value="N">정상 (N)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>정보 확인 여부</InputLabel>
              <Select value={selectedMember.infoConfirmedYn} label="정보 확인 여부" onChange={(e) => setSelectedMember({ ...selectedMember, infoConfirmedYn: e.target.value })}>
                <MenuItem value="Y">확인완료 (Y)</MenuItem>
                <MenuItem value="N">미확인 (N)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>권한 (Role)</InputLabel>
              <Select value={selectedMember.role} label="권한 (Role)" onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}>
                <MenuItem value="ROLE_TEACHER">교원회원 (ROLE_TEACHER)</MenuItem>
                <MenuItem value="ROLE_USER">일반회원 (ROLE_USER)</MenuItem>
                <MenuItem value="ROLE_ADMIN">관리자 (ROLE_ADMIN)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>계정 상태</InputLabel>
              <Select value={selectedMember.delYn} label="계정 상태" onChange={(e) => setSelectedMember({ ...selectedMember, delYn: e.target.value })}>
                <MenuItem value="N">정상 (N)</MenuItem>
                <MenuItem value="Y">탈퇴/정지 (Y)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)}>취소</Button>
          <Button onClick={handleSaveMember} variant="contained">저장</Button>
        </DialogActions>
      </Dialog>

      {/* 엑셀 미리보기 및 일괄 등록 모달 Dialog */}
      <Dialog open={excelModalOpen} onClose={() => setExcelModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>엑셀 일괄 등록 미리보기</span>
          <Chip label={`총 ${excelData.length}건`} color="primary" size="small" />
        </DialogTitle>
        <DialogContent dividers>
          {/* 저장 완료 피드백 알림 영역 */}
          {bulkResult && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={bulkResult.failureCount > 0 ? "warning" : "success"}>
                <AlertTitle>일괄 등록 처리 결과</AlertTitle>
                전체 <strong>{bulkResult.totalCount}</strong>건 중 <strong>{bulkResult.successCount}</strong>건 성공, 
                <strong style={{ color: bulkResult.failureCount > 0 ? 'red' : 'inherit' }}> {bulkResult.failureCount}</strong>건 실패
              </Alert>
              {bulkResult.failures && bulkResult.failures.length > 0 && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fef2f2', borderRadius: '8px', maxHeight: 150, overflowY: 'auto' }}>
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700, mb: 0.5 }}>
                    실패 항목 상세 사유:
                  </Typography>
                  {bulkResult.failures.map((f, idx) => (
                    <Typography key={idx} variant="caption" display="block" color="error">
                      • [{f.index}행] 아이디: {f.loginId} ({f.name}) - {f.reason}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>아이디 *</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>비밀번호 *</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>이름 *</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>이메일 *</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>생년월일</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>연락처</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>주소</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>자격증번호</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>삭제</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ minWidth: 100 }}>
                      <TextField size="small" variant="standard" value={row.loginId} onChange={(e) => handleExcelCellChange(idx, 'loginId', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <TextField size="small" variant="standard" value={row.password} onChange={(e) => handleExcelCellChange(idx, 'password', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      <TextField size="small" variant="standard" value={row.name} onChange={(e) => handleExcelCellChange(idx, 'name', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <TextField size="small" variant="standard" value={row.email} onChange={(e) => handleExcelCellChange(idx, 'email', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      <TextField size="small" variant="standard" value={row.birthDate} onChange={(e) => handleExcelCellChange(idx, 'birthDate', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      <TextField size="small" variant="standard" value={row.phone} onChange={(e) => handleExcelCellChange(idx, 'phone', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField size="small" variant="standard" value={row.address} onChange={(e) => handleExcelCellChange(idx, 'address', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      <TextField size="small" variant="standard" value={row.certNumber} onChange={(e) => handleExcelCellChange(idx, 'certNumber', e.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveExcelRow(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setExcelModalOpen(false)}>닫기</Button>
          <Button
            onClick={handleSaveBulkExcel}
            variant="contained"
            color="primary"
            disabled={bulkSaving || excelData.length === 0}
            startIcon={bulkSaving && <CircularProgress size={16} color="inherit" />}
          >
            {bulkSaving ? '저장 처리 중...' : '일괄 저장하기'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberList;